'use client';

import { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Building2, Bell, Shield, Mail, Target, Pencil, Check, X, CalendarDays, DollarSign } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { BallLevelCriteria } from '@/components/director/ball-level-criteria';
import { TermsSettings } from '@/components/director/terms-settings';
import { SessionRatesSettings } from '@/components/director/session-rates-settings';

export default function DirectorSettingsPage() {
  const org = useQuery(api.director.organization.get);
  const updateOrg = useMutation(api.director.organization.update);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedOk, setSavedOk] = useState(false);

  const [form, setForm] = useState({
    name: '',
    location: '',
    phone: '',
    website: '',
  });

  // Populate form when org data loads
  useEffect(() => {
    if (org) {
      setForm({
        name: org.name,
        location: org.location ?? '',
        phone: org.phone ?? '',
        website: org.website ?? '',
      });
    }
  }, [org]);

  const handleEdit = () => {
    setSaveError(null);
    setSavedOk(false);
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (org) {
      setForm({
        name: org.name,
        location: org.location ?? '',
        phone: org.phone ?? '',
        website: org.website ?? '',
      });
    }
    setIsEditing(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setSaveError('Club name is required.');
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      await updateOrg({
        name: form.name.trim(),
        location: form.location.trim() || undefined,
        phone: form.phone.trim() || undefined,
        website: form.website.trim() || undefined,
      });
      setIsEditing(false);
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
    } catch {
      setSaveError('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>
        <p className="text-gray-600">Manage your club preferences</p>
      </div>

      <div className="grid gap-6">
        {/* Club Settings */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Club Settings
                </CardTitle>
                <CardDescription>
                  Configure your tennis club details
                </CardDescription>
              </div>
              {!isEditing && (
                <Button variant="outline" size="sm" onClick={handleEdit}>
                  <Pencil className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {org === undefined ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : isEditing ? (
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="club-name">Club Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="club-name"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="e.g. Ace Tennis Club"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="club-location">Location</Label>
                    <Input
                      id="club-location"
                      value={form.location}
                      onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                      placeholder="e.g. Sydney, Australia"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="club-phone">Phone</Label>
                    <Input
                      id="club-phone"
                      value={form.phone}
                      onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                      placeholder="e.g. +61 2 9000 0000"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="club-website">Website</Label>
                    <Input
                      id="club-website"
                      value={form.website}
                      onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
                      placeholder="e.g. https://acetennisclub.com"
                    />
                  </div>
                </div>
                {saveError && (
                  <p className="text-sm text-red-500">{saveError}</p>
                )}
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSave} disabled={isSaving}>
                    <Check className="w-4 h-4 mr-1" />
                    {isSaving ? 'Saving…' : 'Save'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancel} disabled={isSaving}>
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label className="text-sm font-medium text-foreground">Club Name</Label>
                  <p className="text-foreground">{org?.name ?? '—'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-foreground">Location</Label>
                  <p className="text-foreground">{org?.location ?? '—'}</p>
                </div>
                {(org?.phone || org?.website) && (
                  <>
                    {org?.phone && (
                      <div>
                        <Label className="text-sm font-medium text-foreground">Phone</Label>
                        <p className="text-foreground">{org.phone}</p>
                      </div>
                    )}
                    {org?.website && (
                      <div>
                        <Label className="text-sm font-medium text-foreground">Website</Label>
                        <p className="text-foreground">{org.website}</p>
                      </div>
                    )}
                  </>
                )}
                {savedOk && (
                  <p className="text-sm text-green-600 col-span-2">Club profile saved.</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ball Level Evaluation Criteria */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Evaluation Criteria by Ball Level
            </CardTitle>
            <CardDescription>
              Define custom evaluation criteria for each ball color level. Up to 6 criteria per level.
              Players without custom criteria will use the default evaluation system.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <BallLevelCriteria />
          </CardContent>
        </Card>

        {/* Terms */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5" />
              Terms
            </CardTitle>
            <CardDescription>
              Define your term periods. Players are enrolled per term and invoices are generated against a selected term.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TermsSettings />
          </CardContent>
        </Card>

        {/* Session Rates */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Session Rates
            </CardTitle>
            <CardDescription>
              Set the term rate for each session type. These rates are applied when generating bulk invoices.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SessionRatesSettings />
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notifications
            </CardTitle>
            <CardDescription>
              Choose what notifications you receive
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>New player registrations</Label>
                <p className="text-sm text-muted-foreground">Get notified when a new player joins</p>
              </div>
              <Switch defaultChecked />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Session reminders</Label>
                <p className="text-sm text-muted-foreground">Receive reminders before sessions</p>
              </div>
              <Switch defaultChecked />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Coach updates</Label>
                <p className="text-sm text-muted-foreground">Get notified about coach activity</p>
              </div>
              <Switch />
            </div>
          </CardContent>
        </Card>

        {/* Privacy & Security */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Privacy & Security
            </CardTitle>
            <CardDescription>
              Manage your security preferences
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Two-factor authentication</Label>
                <p className="text-sm text-muted-foreground">Add an extra layer of security</p>
              </div>
              <Switch />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Session timeout</Label>
                <p className="text-sm text-muted-foreground">Auto-logout after 30 minutes of inactivity</p>
              </div>
              <Switch defaultChecked />
            </div>
          </CardContent>
        </Card>

        {/* Coming Soon */}
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-400">
              <Mail className="w-5 h-5" />
              Email Templates
              <span className="ml-2 px-2 py-0.5 text-xs bg-purple-100 text-purple-600 rounded-full">
                Coming Soon
              </span>
            </CardTitle>
            <CardDescription>
              Customize automated emails sent to parents and coaches
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    </div>
  );
}
